import React, { useCallback, Dispatch, SetStateAction } from 'react';
import { Box } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import { SelectedDateRange } from '..';
import { NumberOfPetParentsReached } from 'shared/types/graphql';
import { formatPluralization } from 'shared/utils';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { MassTextAlertsModalName } from '../records/MassTextAlertsModalName';

interface MessageComposerProps {
  messageText: string;
  setMessageText: Dispatch<SetStateAction<string>>;
  setRemainingCharCount: Dispatch<SetStateAction<number>>;
  selectedDateRange: SelectedDateRange | null;
  remainingCharCount: number;
  sendTimeData?: Pick<NumberOfPetParentsReached, 'numberOfPetPerents' | 'timeEstimateInSeconds'> | null;
  fetchEstimatedSendTime: (startDate?: Date | undefined, endDate?: Date | undefined) => void;
  formatTimeEstimate: () => string | undefined;
  openModal: (modalName: MassTextAlertsModalName) => void;
}

const MassTextAlertsMessageCompose = (props: MessageComposerProps): JSX.Element => {
  const {
    messageText,
    setMessageText,
    selectedDateRange,
    remainingCharCount,
    sendTimeData,
    setRemainingCharCount,
    fetchEstimatedSendTime,
    formatTimeEstimate,
    openModal,
  } = props;

  const isButtonDisabled = useCallback((): boolean => {
    return !selectedDateRange?.optionName || remainingCharCount === 450 || !sendTimeData?.numberOfPetPerents;
  }, [selectedDateRange, remainingCharCount, sendTimeData]);

  const handleTextAreaOnChange = useCallback(
    (e) => {
      const remainingChars = 450 - e.target.value.length;
      if (remainingChars < 0) {
        return;
      }
      setMessageText(e.target.value);
      localStorage.setItem(PersistedStateKeys.MassTextAlertsDraftMessage, e.target.value);
      setRemainingCharCount(remainingChars);
    },
    [setMessageText, setRemainingCharCount],
  );

  const handleOnSendButtonClick = useCallback(() => {
    if (selectedDateRange?.endDate && selectedDateRange.startDate) {
      fetchEstimatedSendTime(selectedDateRange.startDate, selectedDateRange.endDate);
    }
    openModal(MassTextAlertsModalName.SendMassTextAlertConfirmation);
  }, [openModal, fetchEstimatedSendTime, selectedDateRange]);

  return (
    <>
      <Box position="relative" w="auto" h="auto">
        <Textarea
          placeholder="Type a message here"
          onChange={handleTextAreaOnChange}
          minH="237px"
          pb="40px"
          mt="15px"
          bg="background.default"
          _focus={{ borderColor: 'border.focus' }}
          value={messageText}
        />
        <Text variant="subtle" as="p" size="xs" pos="absolute" right="18px" bottom="9px" left={3}>
          {remainingCharCount} {formatPluralization({ word: 'Character', singularCriteria: remainingCharCount })}{' '}
          Remaining
        </Text>
      </Box>
      <Text
        as="p"
        my={2}
        size="xs"
        fontStyle="italic"
        textAlign="center"
        visibility={!isButtonDisabled() ? 'visible' : 'hidden'}
      >
        Estimated sending time: {formatTimeEstimate()}
      </Text>
      <Button size="sm" isFullWidth disabled={isButtonDisabled()} onClick={handleOnSendButtonClick}>
        Send Mass Text Alert
      </Button>
    </>
  );
};

export default MassTextAlertsMessageCompose;
